import { useMemo } from 'react';
import type { ForgeUiAnalyticsAttributes } from '@atlassian/jira-forge-ui-types/src/common/types/analytics.tsx';
import type { CustomUIBridge } from '@atlassian/jira-forge-ui-types/src/common/types/bridge.tsx';
import type { CustomBridgeMethods } from '@atlassian/jira-forge-ui-types/src/common/types/custom-bridge-methods.tsx';
import type { Extension } from '@atlassian/jira-forge-ui-types/src/common/types/extension.tsx';
import type { EntryPoint } from '@atlassian/jira-forge-ui-types/src/common/types/module.tsx';
import { getForgeUIType } from '@atlassian/jira-forge-ui-utils-internal/src/utils/extension/index.tsx';
import { parseExtensionId } from '@atlassian/jira-forge-ui-utils-internal/src/utils/parse-extension-id/index.tsx';
import {
	bridgeReporter,
	customBridgeMethodsReporter,
	type BridgeMethodName,
	type BridgeReporter,
	type CustomBridgeMethodsReporter,
} from '../../services/bridge-methods/index.tsx';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const useWithMetrics = <MethodsObject extends Record<string, (...args: any[]) => any>>(
	methods: MethodsObject | null | undefined,
	extension: Extension,
	entryPoint: EntryPoint | null | undefined,
	reporters: BridgeReporter | CustomBridgeMethodsReporter,
): MethodsObject | undefined | null =>
	useMemo(() => {
		if (methods == null) {
			return methods;
		}

		const wrapped = { ...methods };
		// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
		const keys = Object.keys(wrapped) as Array<Extract<keyof MethodsObject, BridgeMethodName>>;
		const forgeType = getForgeUIType(extension);
		const { id: extensionId, type: moduleType, environmentType: envType } = extension;
		const { appId, envId } = parseExtensionId(extensionId);

		keys.forEach((methodName) => {
			/** Allows passthrough properties that are difened in the bridge like `basePath` `history` */
			if (typeof wrapped[methodName] !== 'function') {
				return;
			}
			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
			wrapped[methodName] = ((...args: Parameters<MethodsObject[typeof methodName]>) => {
				const methodHandler = methods[methodName];
				const fireEvent = <
					Reporter extends (
						...reporterArgs: [
							methodName: BridgeMethodName,
							forgeAnalyticsAttributes: ForgeUiAnalyticsAttributes,
						]
					) => void,
				>(
					reporter: Reporter,
					// eslint-disable-next-line @typescript-eslint/no-explicit-any
					extraAttributes?: { error: any },
				) =>
					reporter(methodName, {
						forgeType,
						appId,
						envId,
						entryPoint,
						moduleType,
						envType,
						...extraAttributes,
					});

				fireEvent(reporters.fireTrackMethodCalledEvent);
				try {
					const callResult = methodHandler(...args);
					Promise.resolve(callResult).catch((error) => {
						fireEvent(reporters.fireOperationalMethodFailedAsyncEvent, { error });
					});
					return callResult;
					// eslint-disable-next-line @typescript-eslint/no-explicit-any
				} catch (error: any) {
					fireEvent(reporters.fireOperationalMethodFailedSyncEvent, { error });
					throw error;
				}
			}) as MethodsObject[typeof methodName];
		});

		return wrapped;
	}, [
		methods,
		reporters.fireTrackMethodCalledEvent,
		reporters.fireOperationalMethodFailedAsyncEvent,
		reporters.fireOperationalMethodFailedSyncEvent,
		extension,
		entryPoint,
	]);

export const useBridgeWithMetrics = (
	bridge: CustomUIBridge | null | undefined,
	extension: Extension,
	entryPoint?: EntryPoint,
): CustomUIBridge | undefined | null =>
	useWithMetrics(bridge, extension, entryPoint, bridgeReporter);

export const useCustomBridgeMethodsWithMetrics = (
	customBridgeMethods: CustomBridgeMethods | null | undefined,
	extension: Extension,
	entryPoint?: EntryPoint,
): CustomBridgeMethods | undefined | null =>
	useWithMetrics(customBridgeMethods, extension, entryPoint, customBridgeMethodsReporter);
